import { ofType } from 'redux-observable';
import { Observable, from } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import { isCountryCode } from '@bridebook/toolbox/src/is';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { AuthActionTypes } from '@bridebook/web/lib/auth/action-types';
import { setSearchLocationEnd } from '@bridebook/web/lib/search/actions';
import { SearchLocationStorage } from '@bridebook/web/lib/search/search-location-storage';
import { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import { IEpicDeps, TSearchLocationInitialized } from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';

const getInitialSearchLocation: () => Promise<TSearchLocationInitialized> = async () => {
  const initialSearchLocation: TSearchLocationInitialized = {
    selected: {
      area: '',
      placeId: '',
      searchText: '',
      searchType: 'autocomplete',
    },
    draft: {
      searchText: '',
      searchType: 'autocomplete',
    },
    status: 'initialized',
  };

  if (typeof window === 'undefined') return initialSearchLocation;

  try {
    const stored = await SearchLocationStorage.get();
    if (stored && isCountryCode(stored.selected.countryCode)) {
      if (!stored.draft.searchText) {
        // Patch legacy persisted country defaults that have blank draft text.
        const countryName = Gazetteer.getCountryName(stored.selected.countryCode);
        return {
          ...stored,
          draft: { ...stored.draft, searchText: countryName },
          selected: {
            ...stored.selected,
            searchText: stored.selected.searchText || countryName,
          },
        };
      }
      return stored;
    }
    return initialSearchLocation;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error parsing SEARCH_LOCATION_STORAGE_KEY', e);
    SentryMinimal().captureException(e, {
      tags: {
        source: 'initSearchLocationEpic',
      },
    });
    return initialSearchLocation;
  }
};

export const initSearchLocation = (action$: Observable<any>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType('APP_STARTED', AuthActionTypes.USER_SETUP_COMPLETED),
    mergeMap(() => {
      const getPromise = async () => {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          return noopAction();
        }
        const initialSearchLocation = await getInitialSearchLocation();

        // Avoid async init overwriting a value just set by the default-location epic.
        const current = state$.value.search.searchLocation;
        const wouldClobber =
          current.status === 'initialized' &&
          !!current.draft.searchText &&
          !initialSearchLocation.draft.searchText;

        if (wouldClobber) {
          return noopAction();
        }
        return setSearchLocationEnd(initialSearchLocation);
      };

      return from(getPromise());
    }),
  );
